import { Box, Checkbox, FormControlLabel, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { CheckIcon, DisabledUncheckedIcon, UnCheckIcon } from "../../../../assets/SvgIcons";
import { checkImageExists, formatCurrency, isModuleAllowed } from "../../../../utils/utilityFunctions";
import { HoldingsTableData } from "./types";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { MPOWER_ACCOUNT_TYPE_MASTER, ROLE_TYPE, USER_ROLES } from "../../../../utils/constant";

function TableItemCheckbox({
    value,
    schemeName,
    schemeImage,
    onCheckboxClick,
    checked,
    amcId,
    folioNo,
    isinCode,
    isPermissionDenied
}: {
    value: string;
    schemeName: string;
    schemeImage: string;
    checked: boolean;
    onCheckboxClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    amcId?: string;
    folioNo: string;
    isinCode: string;
    isPermissionDenied: boolean
}) {
    const {
        subRole = '',
        role = ''
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolioObj
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);

    const amcIds = ['BDOTR', 'BPIAMC']

    const checkAmcId = amcIds.includes(amcId ?? '') ? amcId?.toLowerCase() : amcId;

    // If subRole is not there (Investor case), then => NOT DISABLED
    // If role === IA or subRole === IA, then => NOT DISABLED
    // Otherwise => 
    //    - portfolioAccountType === ADVISORY/POA => NOT DISABLED
    //    - portfolioAccountType !== ADVISORY/POA => DISABLED   

    const isCheckboxDisabled = subRole ?
        (
            isModuleAllowed({ value: `${role ?? ""}`, compareArr: [USER_ROLES.IA] }) ||
            isModuleAllowed({ value: `${subRole ?? ""}`, compareArr: [ROLE_TYPE.IA] })
        ) ? false :
            !([
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${selectedPortfolioObj?.portfolioAccountType ?? ""}`?.toUpperCase()]))
        : false

    return (
        <FormControlLabel
            value={value}
            control={
                <Checkbox
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={(isCheckboxDisabled || isPermissionDenied) ? <DisabledUncheckedIcon /> : <UnCheckIcon />}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    onChange={onCheckboxClick}
                    checked={checked}
                    disabled={
                        isCheckboxDisabled ||
                        isPermissionDenied
                    }
                />
            }
            label={
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "12px",
                        alignItems: "center"
                    }}
                >
                    <img
                        src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                        alt={schemeName}
                        width={35}
                        height={35}
                        style={{
                            objectFit: "contain",
                            borderRadius: "8px"
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            rowGap: 0.5
                        }}
                    >
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    color: theme.palette.primary[500],
                                },
                            })}
                        >
                            {`${schemeName}(${folioNo})`}
                        </Typography>
                        <Typography
                            variant="body_500"
                            color="text.primary"
                        >
                            ISIN:{' '}
                            <Typography
                                component="span"
                                variant="body_500"
                                color="text.secondary"
                            >
                                {isinCode ?? ""}
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            }
            sx={{
                "& .MuiFormControlLabel-label": {
                    marginLeft: "5px"
                }
            }}
        />
    );
}

export default function HoldingsTable({
    data = [],
    onCheckboxClick
}: {
    data: HoldingsTableData[];
    onCheckboxClick: (schemeId: string, checked: boolean, id: string) => void
}): JSX.Element {
    const theme = useTheme();

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const isPermissionDenied = !isModuleAllowed({ value: 'MFOrders', compareArr: permissions, objKey: "permissonCode" }) && !isModuleAllowed({ value: 'OrderRecommend', compareArr: permissions, objKey: "permissonCode" })

    return (
        <>
            {data.map((obj: HoldingsTableData) => (
                <TableRow
                    key={obj.id}
                    sx={{
                        '& td': {
                            paddingTop: "20px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            ':first-of-type': {
                                paddingLeft: "-0.1px",
                            }
                        },
                    }}
                >
                    <TableCell
                        align="left"
                        sx={{ paddingLeft: "2px !important" }}
                    >
                        <TableItemCheckbox
                            value={obj.schemeId}
                            schemeName={obj.schemeName}
                            schemeImage={obj.schemeLogo}
                            onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => onCheckboxClick(obj.schemeId, event.target.checked, obj.id)}
                            checked={obj.checked}
                            amcId={obj.amcId}
                            folioNo={obj.folioNo ?? ''}
                            isinCode={obj?.isinCode ?? ""}
                            isPermissionDenied={isPermissionDenied}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {!Number.isNaN(Number(obj.units)) ?
                                Math.round(obj.units * 100) / 100 : '-'}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj.investedValue < 0 ? `- ${formatCurrency({ value: Math.abs(obj.investedValue), locale, currencyCode })}` : `${formatCurrency({ value: obj.investedValue, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj.marketValue < 0 ? `- ${formatCurrency({ value: Math.abs(obj.marketValue), locale, currencyCode })}` : `${formatCurrency({ value: obj.marketValue, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: obj.percentage.amount >= 0 ? "success.800" : "error.500",
                            }}
                        >
                            {obj.percentage.amount < 0 ? `- ${formatCurrency({ value: Math.abs(obj.percentage.amount), locale, currencyCode })}` : `${formatCurrency({ value: obj.percentage.amount, locale, currencyCode })}`} ({!Number.isNaN(Number(obj.percentage.percentage)) ?
                                Math.round(obj.percentage.percentage * 100) / 100 : ''}%)
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: obj.irr >= 0 ? "success.800" : "error.500",
                            }}
                        >
                            {!Number.isNaN(Number(obj.irr)) ?
                                Math.round(obj.irr * 100) / 100 : ''}%
                        </Typography>
                    </TableCell>
                </TableRow>
            ))
            }
        </>
    )
}